import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ChartQuery} from 'entities/chart';
import {PivotCellChartData} from 'entities/chartData';
import useData from '@/utils/useData';
import {EventFilter, EventFilterGroup} from '@/views/storyboard/utils/chartConstants';
import {pivotCellsToMatrix} from '@/views/storyboard/charts/utils/pivotCellsToMatrix';
import PivotTableContent from './PivotTableContent';
import ChartIndicators from '@/views/storyboard/charts/utils/ChartIndicators';
import {ChartFooterData} from '@/views/storyboard/charts/utils/chartFooterData';
import i18n from 'i18n';

interface Props {
    chartId?: number | null;
    data: PivotCellChartData[];
    chartQuery: ChartQuery;
    chartTitle: string;
    queryDuration?: string;
    chartSettingsView?: boolean;
    onPreviewFooterChange?: (footerData: ChartFooterData) => void;
}

function useContainerSize(ref: React.RefObject<HTMLDivElement | null>): {width: number; height: number} {
    const [size, setSize] = useState({width: 0, height: 0});

    const measure = useCallback(() => {
        if (ref.current) {
            const {width, height} = ref.current.getBoundingClientRect();
            setSize((prev) =>
                prev.width === Math.round(width) && prev.height === Math.round(height)
                    ? prev
                    : {width: Math.round(width), height: Math.round(height)},
            );
        }
    }, [ref]);

    useEffect(() => {
        measure();
        if (!ref.current) return;

        const observer = new ResizeObserver(measure);
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [ref, measure]);

    return size;
}

export const PivotChartWrapper = React.memo<Props>((
    {
        data,
        chartQuery,
        chartId = null,
        chartTitle,
        queryDuration = '0',
        chartSettingsView = false,
        onPreviewFooterChange,
    },
) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const {width, height} = useContainerSize(containerRef);

    const [eventFilterGroupList] = useData<EventFilterGroup>(window.URLS.eventFiltersResource, {
        storeResolver: 'eventFilters',
        actionResolver: 'loadEventFilters',
        useCachedData: true,
    });

    const eventFilters: EventFilter[] = [
        ...(eventFilterGroupList.core || []),
        ...(eventFilterGroupList.prioritised || []),
        ...(eventFilterGroupList.non_prioritised || []),
        ...(eventFilterGroupList.others || []),
    ];

    const matrix = useMemo(() => pivotCellsToMatrix(data), [data]);

    const rowHeader = eventFilters.find((field) => field[0] === chartQuery.aggs_item)?.[1]
        ?? i18n.t('pivot_row_field.text');

    return (
        <>
            <div ref={containerRef} style={{flex: 1, minHeight: 0, width: '100%'}}>
                {width > 0 && height > 0 && (
                    <PivotTableContent
                        width={width}
                        height={height}
                        matrix={matrix}
                        rowHeader={rowHeader}
                        chartId={chartId}
                        chartTitle={chartTitle}
                    />
                )}
            </div>
            <ChartIndicators
                total={matrix.grandTotal}
                totalComparable={0}
                trendValue="0"
                queryDuration={queryDuration}
                chartQueryOutput={chartQuery.output}
                aggregationType={chartQuery.metric_aggregations_type}
                chartSettingsView={chartSettingsView}
                onPreviewFooterChange={onPreviewFooterChange}
            />
        </>
    );
});

export default PivotChartWrapper;
